import React, { useState, useEffect } from 'react';
import { Web3 } from 'web3';
import { fetchLiquidityInfo, fetchPoolInfo } from './poolUtils';
import { formatNumberM } from '../utils/formatNumber';
import LiquiditySegmentChart from './LiquiditySegmentChart';
import { presetConfigurations } from './config';

// Add keyframes animation for the gradient effect
const gradientKeyframes = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes shine {
    0% { text-shadow: 0 0 5px rgba(0,0,0,0.8), 0 0 10px rgba(255,255,255,0.8); }
    25% { text-shadow: 0 0 5px rgba(0,0,0,0.8), 0 0 20px #ff00ff, 0 0 30px #ff00ff; }
    50% { text-shadow: 0 0 5px rgba(0,0,0,0.8), 0 0 20px #00ffff, 0 0 30px #00ffff; }
    75% { text-shadow: 0 0 5px rgba(0,0,0,0.8), 0 0 20px #ffff00, 0 0 30px #ffff00; }
    100% { text-shadow: 0 0 5px rgba(0,0,0,0.8), 0 0 10px rgba(255,255,255,0.8); }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes rotate {
    0% { filter: hue-rotate(0deg); }
    100% { filter: hue-rotate(360deg); }
  }
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
`;

// LiquidHealth component
function LiquidHealth({ effectiveLiquidityUSD, liquidityRatio, currentConfig }) {
  // Define the tag properties based on the effectiveLiquidityUSD value
  let label, style;
  
  // 首先检查是否有healthOverride配置，并且流动性比值落在effentive区间内
  if (currentConfig && currentConfig.healthOverride && Array.isArray(currentConfig.healthOverride)) {
    for (const override of currentConfig.healthOverride) {
      if (override.effentive && Array.isArray(override.effentive) && override.effentive.length === 2) {
        const [minRatio, maxRatio] = override.effentive;
        // 检查流动性比值是否落在override的区间内
        if (liquidityRatio >= minRatio && liquidityRatio <= maxRatio) {
          label = override.label;
          // 使用危险样式
          style = {
            backgroundColor: '#dc3545',
            color: 'white',
            textShadow: '0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black'
          };
          break; // 找到匹配的override后退出循环
        }
      }
    }
  }
  
  // 如果没有找到匹配的override，使用默认逻辑
  if (!label) {
    if (effectiveLiquidityUSD < 1000) {
      label = "危险";
      style = {
        backgroundColor: '#dc3545',
        color: 'white',
        textShadow: '0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black'
      };
    } else if (effectiveLiquidityUSD < 10000) {
      label = "有风险";
      style = {
        backgroundColor: '#ffc107',
        color: 'black',
        textShadow: '0px 0px 1px white, 0px 0px 1px white, 0px 0px 1px white, 0px 0px 1px white'
      };
    } else if (effectiveLiquidityUSD < 100000) {
      label = "健康";
      style = {
        backgroundColor: '#28a745',
        color: 'white',
        textShadow: '0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black, 0px 0px 1px black'
      };
    } else {
      label = "优秀";
      style = {
        background: 'linear-gradient(270deg, #ff0000, #ff9900, #ffff00, #33cc33, #0066ff, #9900cc, #ff00ff)',
        backgroundSize: '1000% 1000%',
        animation: 'gradient 3s ease infinite, shine 2s linear infinite, pulse 1.5s ease-in-out infinite, rotate 4s linear infinite, blink 1s ease-in-out infinite',
        color: 'white',
        textShadow: '0 0 5px rgba(0,0,0,0.8), 0 0 10px rgba(255,255,255,0.8), 0 0 15px #ff00ff',
        boxShadow: '0 0 10px rgba(255,255,255,0.5), inset 0 0 10px rgba(255,255,255,0.2), 0 0 20px rgba(255,0,255,0.7)',
        border: '1px solid rgba(255,255,255,0.6)',
        transition: 'all 0.3s ease',
        fontWeight: 'bold',
        backdropFilter: 'blur(1px)',
        letterSpacing: '1px'
      };
    }
  }
  
  return (
    <>
      <style>{gradientKeyframes}</style>
      <span style={{
        ...style,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'inline-block',
        marginLeft: '8px'
      }}>
        {label}
      </span>
    </>
  );
}

function LiquidPoolInfo({ poolAddress }) {
  const [web3] = useState(() => new Web3('https://bsc-dataseed.bnbchain.org'));
  const [poolInfo, setPoolInfo] = useState({
    token0: '',
    token1: '',
    token0Name: '',
    token1Name: '',
    fee: 0
  });
  const [liquidityInfo, setLiquidityInfo] = useState({
    token0Balance: '0',
    token1Balance: '0',
    token0Price: 0,
    token1Price: 0,
    totalLiquidityUSD: 0,
    currentPrice: 0,
    effectiveLiquidityUSD: 0,
    liquidssegments: []
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // 根据poolAddress匹配对应的config配置
  const currentConfig = poolAddress ? presetConfigurations.find(config => 
    config.poolAddress && config.poolAddress.toLowerCase() === poolAddress.toLowerCase()
  ) : null;
  
  // Fetch pool info when poolAddress changes
  useEffect(() => {
    const getPoolInfo = async () => {
      if (!poolAddress || !web3.utils.isAddress(poolAddress)) return;
      
      try {
        const info = await fetchPoolInfo(web3, poolAddress);
        setPoolInfo(info);
      } catch (error) {
        console.error('Error fetching pool info:', error);
      }
    };
    
    getPoolInfo();
  }, [poolAddress, web3]);
  
  // Effect to monitor pool info changes and fetch liquidity
  useEffect(() => {
    let intervalId = null;
    
    const fetchLiquidityData = async () => {
      if (!poolInfo.token0 || !poolInfo.token1 || !poolAddress) return;
      
      try {
        const result = await fetchLiquidityInfo(web3, poolAddress);
        if (result) {
          setLiquidityInfo(result);
        }
        
      } catch (error) {
        console.error('Error fetching liquidity info:', error);
      } finally {
      }
      const result = await fetchLiquidityInfo(web3, poolAddress,false)
      if (result) {
        setLiquidityInfo(result);
      }
    };
    
    // Initial fetch when pool info is available
    fetchLiquidityData();
    
    // Set up interval to refresh liquidity every 30 seconds
    if (poolInfo.token0 && poolInfo.token1 && poolAddress) {
      intervalId = setInterval(fetchLiquidityData, 30e3);
    }
    
    // Cleanup interval on unmount or when pool info changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [poolInfo.token0, poolInfo.token1, poolAddress, web3]);

  // If no pool address or loading, show appropriate UI
  if (!poolAddress) return null;
  
  if (!poolInfo.token0 || !poolInfo.token1) {
    return (
      <div style={{ 
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#e8f5e8',
        borderRadius: '5px',
        border: '1px solid #c3e6cb'
      }}>
        <h4 style={{ color: '#155724', margin: '0 0 10px 0', fontSize: '14px' }}>
          💰 池子流动性信息
        </h4>
        <div style={{ fontSize: '13px', color: '#155724' }}>
          加载池子信息中...
        </div>
      </div>
    );
  }
  
  // Check if the address in the liquidity info matches the current poolAddress
  const shouldDisplayLiquidityInfo = !liquidityInfo.poolAddress || liquidityInfo.poolAddress.toLowerCase() === poolAddress.toLowerCase();
  
  // 计算流动性比值
  const liquidityRatio = liquidityInfo.totalLiquidityUSD > 0 
    ? liquidityInfo.effectiveLiquidityUSD / liquidityInfo.totalLiquidityUSD 
    : 0;
  
  return (
    <div style={{ 
      marginTop: '10px',
      padding: '10px',
      backgroundColor: '#e8f5e8',
      borderRadius: '5px',
      border: '1px solid #c3e6cb'
    }}>
      <h4 style={{ color: '#155724', margin: '0 0 10px 0', fontSize: '14px' }}>
        💰 池子流动性信息
      </h4>
      
      {!shouldDisplayLiquidityInfo ? (
        <div style={{ fontSize: '13px', color: '#155724' }}>加载流动性信息中...</div>
      ) : (
        <div style={{ fontSize: '13px', color: '#155724' }}>
          <div><strong>代币:</strong> {poolInfo.token0Name} / {poolInfo.token1Name}</div>
          <div><strong>手续费:</strong> {poolInfo.fee / 10000}%</div>
          <div><strong>总流动性:</strong> ${formatNumberM(liquidityInfo.totalLiquidityUSD)}</div>
          <div>
            <strong>有效流动性 (0.1% 范围):</strong> ${formatNumberM(liquidityInfo.effectiveLiquidityUSD)}
            <LiquidHealth 
              effectiveLiquidityUSD={liquidityInfo.effectiveLiquidityUSD} 
              liquidityRatio={liquidityRatio}
              currentConfig={currentConfig}
            />
          </div>
          <div><strong>有效流动性/总流动性:</strong> {(liquidityRatio * 100).toFixed(2)}%</div>
          <div><strong>当前价格:</strong> {liquidityInfo.currentPrice.toFixed(8)}</div>
          <div>
            <strong>池子构成:</strong> {formatNumberM(parseFloat(liquidityInfo.token0Balance))} {poolInfo.token0Name} / 
            {formatNumberM(parseFloat(liquidityInfo.token1Balance))} {poolInfo.token1Name}
          </div>
        </div>
      )}
      
      {shouldDisplayLiquidityInfo && (
        <LiquiditySegmentChart liquidityInfo={liquidityInfo} />
      )}
    </div>
  );
}

export default LiquidPoolInfo; 